import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { BrandLogo, BrandIcon } from "@/components/Brand";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

// The Emergent-managed OAuth page cannot be visually customized. This
// intermediate branded page gives the user the Spanish, on-brand experience
// they expect before the mandatory Google hand-off. Only one CTA lives
// here: "Iniciar sesión con Google" — everything else is copy and brand.
export default function Login() {
    const [params] = useSearchParams();
    const nextPath = sanitizeNext(params.get("next"));
    const [redirecting, setRedirecting] = useState(false);

    const handleGoogleLogin = () => {
        setRedirecting(true);
        const redirectUrl = window.location.origin + nextPath;
        window.location.href =
            EMERGENT_AUTH_URL +
            "?redirect=" +
            encodeURIComponent(redirectUrl);
    };

    return (
        <div
            data-testid="login-page-root"
            className="min-h-[100dvh] w-full flex flex-col bg-[var(--bg)] text-[var(--ink)]"
        >
            {/* Sutil enlace de vuelta a la landing */}
            <div className="w-full max-w-[520px] mx-auto pt-6 px-6 md:pt-10">
                <a
                    href="/"
                    data-testid="login-back-home"
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors"
                >
                    <ChevronLeft size={14} />
                    Volver
                </a>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-[440px]">
                    {/* Isotipo + wordmark centrados */}
                    <div className="flex flex-col items-center gap-3 mb-10">
                        <BrandIcon
                            variant="dark"
                            data-testid="login-icon"
                            style={{ maxHeight: 64, height: 64, width: "auto" }}
                        />
                        <BrandLogo
                            variant="dark"
                            data-testid="login-logo"
                            style={{ maxHeight: 24, height: 24, width: "auto" }}
                        />
                    </div>

                    {/* Card principal */}
                    <div
                        className="rounded-2xl bg-white px-8 py-10 md:px-10 md:py-12 text-center"
                        style={{
                            boxShadow:
                                "0 1px 2px rgba(20,16,12,0.03), 0 8px 32px -8px rgba(20,16,12,0.08)",
                            border: "1px solid var(--line)",
                        }}
                    >
                        <h1
                            data-testid="login-heading"
                            className="font-serif text-3xl md:text-4xl leading-tight"
                        >
                            Bienvenido a Loving Angels
                        </h1>
                        <p className="mt-3 text-sm md:text-[15px] text-[var(--ink-soft)] leading-relaxed max-w-[340px] mx-auto">
                            Crea y gestiona tu invitación digital de boda de
                            forma fácil.
                        </p>

                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            disabled={redirecting}
                            data-testid="login-google-btn"
                            className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[var(--ink)] text-[#fbf9f6] py-3.5 px-6 text-[14px] font-medium hover:opacity-90 disabled:opacity-60 transition-all"
                        >
                            {redirecting ? (
                                <>
                                    <Loader2
                                        size={16}
                                        className="animate-spin"
                                    />
                                    Redirigiendo...
                                </>
                            ) : (
                                <>
                                    <GoogleGlyph />
                                    Iniciar sesión con Google
                                </>
                            )}
                        </button>

                        <p className="mt-6 text-[11px] text-[var(--ink-soft)] leading-relaxed">
                            Al continuar, aceptas nuestros{" "}
                            <a
                                href="/terms"
                                className="underline underline-offset-2 hover:text-[var(--ink)]"
                                data-testid="login-terms-link"
                            >
                                Términos de Servicio
                            </a>{" "}
                            y{" "}
                            <a
                                href="/privacy"
                                className="underline underline-offset-2 hover:text-[var(--ink)]"
                                data-testid="login-privacy-link"
                            >
                                Política de Privacidad
                            </a>
                            .
                        </p>
                    </div>

                    <p className="text-center text-[11px] text-[var(--ink-soft)] mt-8 tracking-[0.16em] uppercase">
                        Un solo pago · Publica cuando estés lista
                    </p>
                </div>
            </div>
        </div>
    );
}

// Prevent open-redirect: only allow relative internal paths as `next`.
function sanitizeNext(raw) {
    if (!raw) return "/editor";
    if (!raw.startsWith("/") || raw.startsWith("//")) return "/editor";
    return raw;
}

// Google "G" glyph rendered as inline SVG to avoid an extra request.
function GoogleGlyph() {
    return (
        <svg
            viewBox="0 0 48 48"
            width={18}
            height={18}
            aria-hidden="true"
            className="shrink-0"
        >
            <path
                fill="#FFC107"
                d="M43.61 20.08H42V20H24v8h11.3c-1.65 4.66-6.08 8-11.3 8-6.63 0-12-5.37-12-12s5.37-12 12-12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.11 29.27 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.65-.39-3.92z"
            />
            <path
                fill="#FF3D00"
                d="M6.31 14.69l6.57 4.82C14.66 15.4 18.99 12 24 12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.11 29.27 4 24 4 16.32 4 9.66 8.34 6.31 14.69z"
            />
            <path
                fill="#4CAF50"
                d="M24 44c5.17 0 9.86-1.98 13.41-5.2l-6.19-5.24C29.14 34.85 26.71 36 24 36c-5.2 0-9.62-3.33-11.28-7.96l-6.53 5.03C9.5 39.62 16.19 44 24 44z"
            />
            <path
                fill="#1976D2"
                d="M43.61 20.08H42V20H24v8h11.3c-.79 2.24-2.24 4.16-4.09 5.56l6.19 5.24C36.98 40.15 44 34 44 24c0-1.34-.14-2.65-.39-3.92z"
            />
        </svg>
    );
}
